/**
 * Mode « embarqué » : la borne « Mon Premier Pari » est aussi intégrée en iframe
 * dans l'application mobile grand public (Capacitor). Dans ce mode, il n'y a ni
 * imprimante, ni plein écran forcé, ni retour automatique d'inactivité : le pari
 * est renvoyé à l'app parente (postMessage) qui affiche le ticket QR et l'historise.
 */

// Contexte d'intégration stocké en sessionStorage (isolé à cette iframe/onglet) :
// une borne physique ouverte sur le MÊME origine partage le localStorage, ce qui
// écraserait le jeton et le drapeau embarqué. sessionStorage évite toute collision.
export const EMBED_STORAGE_KEY = 'kiosk_embed'
export const VISITOR_STORAGE_KEY = 'kiosk_visitor'

export interface EmbedVisitor {
    email?: string
    first_name?: string
    last_name?: string
}

/** Vrai lorsqu'on tourne dans l'app (iframe) plutôt que sur une borne physique. */
export function isEmbedded(): boolean {
    // 1) Détection fiable et isolée : rendu dans une iframe.
    try {
        if (window.self !== window.top) return true
    } catch {
        // L'accès cross-origin à window.top lève : c'est qu'on est bien en iframe.
        return true
    }

    // 2) Drapeau explicite passé en query (?embed=1).
    const embedQuery = new URLSearchParams(window.location.search).get('embed')
    if (embedQuery === '1' || embedQuery === 'true') return true

    // 3) Drapeau mémorisé pour cet onglet après passage sur /setup.
    return sessionStorage.getItem(EMBED_STORAGE_KEY) === '1'
}

/** Identité du parieur connecté transmise par l'app (facultatif). */
export function getVisitor(): EmbedVisitor | null {
    const raw = sessionStorage.getItem(VISITOR_STORAGE_KEY)
    if (!raw) return null
    try {
        const parsed = JSON.parse(raw) as EmbedVisitor
        return parsed && (parsed.email || parsed.first_name || parsed.last_name) ? parsed : null
    } catch {
        return null
    }
}

/** Mémorise le contexte d'intégration transmis via les query params de /setup. */
export function captureEmbedContext(query: Record<string, unknown>): void {
    const embed = firstValue(query.embed)
    if (embed === '1' || embed === 'true') {
        sessionStorage.setItem(EMBED_STORAGE_KEY, '1')
    }

    const visitor: EmbedVisitor = {
        email: firstValue(query.visitor_email) ?? undefined,
        first_name: firstValue(query.visitor_first_name) ?? undefined,
        last_name: firstValue(query.visitor_last_name) ?? undefined,
    }

    if (visitor.email || visitor.first_name || visitor.last_name) {
        sessionStorage.setItem(VISITOR_STORAGE_KEY, JSON.stringify(visitor))
    }
}

/** Notifie l'app parente qu'un pari a été enregistré (pour l'afficher + l'historiser). */
export function notifyParentBet(bet: unknown): void {
    postToParent({ type: 'kiosk:bet', bet })
}

/** Notifie l'app parente d'une demande de fermeture (retour à l'accueil de l'app). */
export function notifyParentClose(): void {
    postToParent({ type: 'kiosk:close' })
}

function postToParent(message: Record<string, unknown>): void {
    try {
        // Clone en objet simple : `postMessage` ne peut pas cloner un proxy réactif
        // Vue (DataCloneError), ce qui ferait échouer l'envoi en silence.
        const plain = JSON.parse(JSON.stringify(message))
        window.parent?.postMessage(plain, '*')
    } catch {
        // Silencieux : hors iframe, il n'y a pas de parent à notifier.
    }
}

function firstValue(value: unknown): string | null {
    if (Array.isArray(value)) return typeof value[0] === 'string' ? value[0].trim() : null
    return typeof value === 'string' ? value.trim() : null
}
